import { useState, useCallback, useEffect, lazy, Suspense } from "react";
import { Routes, Route, useNavigate, useSearchParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { useTranslation } from "react-i18next";
import "./App.css";
import Navbar from "./components/Navbar";
import Header from "./components/Header";
import FlightTables from "./components/FlightTables";
import Footer from "./components/Footer";
import HomePage from "./components/HomePage";
import CountryPage from "./components/CountryPage";
import AboutPage from "./components/AboutPage";
import ContactPage from "./components/ContactPage";
import BookFlight from "./components/BookFlight";
import HubSelector from "./components/HubSelector";
import ShareQR from "./components/ShareQR";
import FlightIcon from "@mui/icons-material/Flight";
import { REGIONAL_AIRSPACE, AIRPORTS, API_CONFIG } from "./constants";
import { findAirlineByCode, findAirlineByName } from "./airlineRegistry";
import { isFuzzyMatch } from "./utils";
import useWeather from "./useWeather";

// Lazy load MapView - Leaflet is the heaviest dependency
const MapView = lazy(() => import("./components/MapView"));

function App() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedHub, setSelectedHub] = useState(() => {
    const saved = localStorage.getItem("rah_selectedHub");
    return saved || REGIONAL_AIRSPACE.hubCode;
  });
  const [previousHub, setPreviousHub] = useState(() => {
    const saved = localStorage.getItem("rah_selectedHub");
    return saved || REGIONAL_AIRSPACE.hubCode;
  });
  const [flightsData, setFlightsData] = useState({
    departures: [],
    arrivals: [],
  });
  const [regionalFlightsData, setRegionalFlightsData] = useState({
    departures: [],
    arrivals: [],
  });
  const [searchQuery, setSearchQuery] = useState("");
  const [searchError, setSearchError] = useState("");
  const [searchModalOpen, setSearchModalOpen] = useState(false);
  const [bookFlightOpen, setBookFlightOpen] = useState(false);
  const [hubModalOpen, setHubModalOpen] = useState(false);

  const { weatherData, weatherLoading } = useWeather(selectedHub);

  // Warm up the tile proxy Lambda before the map mounts
  // Prevents cold-start grey patches on first load
  useEffect(() => {
    fetch(`${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.MAP_TILES}/3/4/3`).catch(() => {});
  }, []);

  // Read search query from URL on mount
  useEffect(() => {
    const urlQuery = searchParams.get("search");
    if (urlQuery) {
      setSelectedHub(REGIONAL_AIRSPACE.hubCode);
      setSearchQuery(urlQuery.toLowerCase());
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Update URL when search query changes
  useEffect(() => {
    if (searchQuery) {
      setSearchParams({ search: searchQuery }, { replace: true });
    } else {
      if (searchParams.has("search")) {
        setSearchParams({}, { replace: true });
      }
    }
  }, [searchQuery, searchParams, setSearchParams]);

  // Memoize the callback to prevent unnecessary re-renders
  const handleSelectHub = useCallback((hub) => {
    setSelectedHub(hub);
    setPreviousHub(hub);
    setSearchQuery("");
    setSearchError("");
    localStorage.setItem("rah_selectedHub", hub);
  }, []);

  const handleFlightsUpdate = useCallback((flights, hubCode) => {
    setFlightsData(flights);
    // Always update regionalFlightsData when we get Regional Airspace data
    if (hubCode === REGIONAL_AIRSPACE.hubCode) {
      setRegionalFlightsData(flights);
    }
  }, []);

  const handleSearch = useCallback(
    (query) => {
      if (!query || query.trim() === "") {
        setSearchQuery("");
        setSelectedHub(previousHub);
        setSearchError("");
        setSearchModalOpen(false);
        return;
      }

      const trimmedQuery = query.trim();
      const lowerQuery = trimmedQuery.toLowerCase();

      // Check if searching by airport code (3 letters) - do this BEFORE airline code check
      if (trimmedQuery.length === 3) {
        const airport = AIRPORTS.find(
          (a) => a.iataCode.toLowerCase() === lowerQuery,
        );
        if (airport) {
          setSearchQuery("");
          setSelectedHub(airport.hubCode);
          setSearchError("");
          setSearchModalOpen(false);
          return;
        }
      }

      // Issue #12: Check if query is an airline code OR airline name
      let processedQuery = trimmedQuery;
      let isAirlineSearch = false;

      // First check if it's an airline code (IATA or ICAO)
      const airlineByCode = findAirlineByCode(lowerQuery);

      if (airlineByCode) {
        processedQuery = airlineByCode.name;
        isAirlineSearch = true;
      } else {
        // Check if it's an airline name (full or partial match)
        const airlineByName = findAirlineByName(lowerQuery);

        if (airlineByName) {
          processedQuery = airlineByName.name;
          isAirlineSearch = true;
        }
      }

      // Check if it's a flight number pattern (letters followed by numbers)
      const flightNumberPattern = /^[a-z]{2,3}\d+$/i;
      const isFlightNumber = flightNumberPattern.test(trimmedQuery);

      if (isFlightNumber || isAirlineSearch) {
        // For flight/airline searches, always use the most complete data available
        let searchData = flightsData;

        // If flightsData is empty but regionalFlightsData has data, use that
        if (
          !flightsData.departures.length &&
          !flightsData.arrivals.length &&
          (regionalFlightsData.departures.length ||
            regionalFlightsData.arrivals.length)
        ) {
          searchData = regionalFlightsData;
        }

        // Issue #3: Better error messages - check if data is loading
        if (!searchData.departures.length && !searchData.arrivals.length) {
          setSearchQuery("");
          setSearchError(t("search.errorLoading"));
          return;
        }

        if (isAirlineSearch) {
          // For airline search, just set the query and let FlightTables filter
          setSelectedHub(REGIONAL_AIRSPACE.hubCode);
          setSearchQuery(processedQuery.toLowerCase());
          setSearchError("");
          setSearchModalOpen(false);
          return;
        }

        // For flight number search, check if flight exists
        const allFlights = [...searchData.departures, ...searchData.arrivals];

        // Issue #1: Exact match priority - find exact matches first for flight numbers
        let matchingFlight = allFlights.find((flight) => {
          const flightNumber = flight.flight?.iata?.toLowerCase() || "";
          return flightNumber === lowerQuery;
        });

        // If no exact match, try partial match
        if (!matchingFlight) {
          matchingFlight = allFlights.find((flight) => {
            const flightNumber = flight.flight?.iata?.toLowerCase() || "";
            return flightNumber.includes(lowerQuery);
          });
        }

        if (matchingFlight) {
          // Match found - switch to Regional Airspace and filter
          setSelectedHub(REGIONAL_AIRSPACE.hubCode);
          setSearchQuery(lowerQuery);
          setSearchError("");
          setSearchModalOpen(false);
        } else {
          // Issue #3: Better error messages - explain why flight not found
          setSearchQuery("");
          setSearchError(
            t("search.errorFlightNotFound", {
              query: processedQuery.toUpperCase(),
            }),
          );
        }
        return;
      }

      // Issue #6: Fuzzy matching for city/country searches
      const matchedAirport = AIRPORTS.find((airport) => {
        const cityName = airport.city?.split(",")[0]?.trim().toLowerCase();
        const countryName = airport.country?.toLowerCase();

        // Exact matches
        const cityMatch = cityName === lowerQuery;
        const countryMatch = countryName === lowerQuery;

        // French country mapping
        const frenchCountryMap = {
          guinée: "guinea",
          guinee: "guinea",
          "côte d'ivoire": "ivory coast",
          "cote d'ivoire": "ivory coast",
          maroc: "morocco",
          sénégal: "senegal",
          senegal: "senegal",
          nigéria: "nigeria",
          nigeria: "nigeria",
        };
        const frenchCountryMatch = frenchCountryMap[lowerQuery] === countryName;

        // Fuzzy matches (for typos)
        const fuzzyCity = isFuzzyMatch(lowerQuery, cityName);
        const fuzzyCountry = isFuzzyMatch(lowerQuery, countryName);

        return (
          cityMatch ||
          countryMatch ||
          frenchCountryMatch ||
          fuzzyCity ||
          fuzzyCountry
        );
      });

      if (matchedAirport) {
        setSearchQuery("");
        setSelectedHub(matchedAirport.hubCode);
        setSearchError("");
        setSearchModalOpen(false);
      } else {
        // Issue #3: Better error messages - explain tracked airports
        setSearchQuery("");
        setSearchError(
          t("search.errorAirportNotTracked", { query: processedQuery }),
        );
      }
    },
    [previousHub, flightsData, regionalFlightsData, t],
  );

  // Issue #4: Clear search function
  const handleClearSearch = useCallback(() => {
    setSearchQuery("");
    setSelectedHub(previousHub);
    setSearchError("");
  }, [previousHub]);

  const handleEnterDashboard = useCallback(() => {
    setHubModalOpen(true);
  }, []);

  const handleSearchFlight = useCallback(
    (query) => {
      if (query) {
        setSelectedHub(REGIONAL_AIRSPACE.hubCode);
        setSearchQuery(query.toLowerCase());
      }
      navigate("/dashboard");
    },
    [navigate],
  );

  const handleLogoHome = useCallback(() => {
    navigate("/");
    setSearchQuery("");
    setSearchError("");
    setSelectedHub(REGIONAL_AIRSPACE.hubCode);
  }, [navigate]);

  const handleHubSelect = useCallback(
    (hubCode) => {
      setSelectedHub(hubCode);
      setPreviousHub(hubCode);
      setHubModalOpen(false);
      localStorage.setItem("rah_selectedHub", hubCode);
      navigate("/dashboard");
    },
    [navigate],
  );

  const dashboardView = (
    <div className="app">
      <Helmet>
        <title>
          {t("dashboard.pageTitle", { defaultValue: "Dashboard - RenuAirHub" })}
        </title>
        <meta
          name="description"
          content="Live flight departures and arrivals dashboard for African airports. Real-time FIDS with weather, delays, gate info, and interactive map."
        />
        <meta property="og:title" content="Dashboard - RenuAirHub" />
        <meta property="og:url" content="https://renuairhub.com/dashboard" />
        <link rel="canonical" href="https://renuairhub.com/dashboard" />
      </Helmet>
      <Navbar
        selectedHub={selectedHub}
        onSelectHub={handleSelectHub}
        onSearch={handleSearch}
        searchError={searchError}
        onClearSearchError={() => setSearchError("")}
        searchModalOpen={searchModalOpen}
        onSearchModalChange={setSearchModalOpen}
        flightsData={flightsData}
        regionalFlightsData={regionalFlightsData}
        onLogoHome={handleLogoHome}
        onBookFlightOpen={() => setBookFlightOpen(true)}
      />
      <Header
        selectedHub={selectedHub}
        weatherData={weatherData}
        weatherLoading={weatherLoading}
      />
      <div className="content-wrapper">
        <Suspense fallback={<div className="map-loading-placeholder" />}>
          <MapView
            selectedHub={selectedHub}
            onSelectHub={handleSelectHub}
            flightsData={flightsData}
          />
        </Suspense>
        <div className="travel-banner">
          <div className="travel-banner-text">
            <div className="travel-banner-icons">
              <FlightIcon style={{ fontSize: 22 }} />
            </div>
            <div>
              <strong>{t("dashboard.planTrip")}</strong>
              <p>{t("dashboard.planTripDesc")}</p>
            </div>
          </div>
          <div className="travel-banner-actions">
            <button
              className="travel-btn travel-btn-flight"
              onClick={() => setBookFlightOpen(true)}
            >
              <FlightIcon style={{ fontSize: 22 }} />{" "}
              {t("dashboard.compareFlights")}
            </button>
            <ShareQR />
          </div>
        </div>
        <FlightTables
          selectedHub={selectedHub}
          onFlightsUpdate={handleFlightsUpdate}
          searchQuery={searchQuery}
          onClearSearch={handleClearSearch}
        />
      </div>
      <Footer />
    </div>
  );

  return (
    <>
      <Routes>
        <Route
          path="/"
          element={
            <HomePage
              onEnterDashboard={handleEnterDashboard}
              onSearchFlight={handleSearchFlight}
              onBookFlightOpen={() => setBookFlightOpen(true)}
            />
          }
        />
        <Route path="/dashboard" element={dashboardView} />
        <Route path="/countries/:countrySlug" element={<CountryPage />} />
        <Route path="/about" element={<AboutPage />} />
        <Route path="/contact" element={<ContactPage />} />
        <Route
          path="*"
          element={
            <HomePage
              onEnterDashboard={handleEnterDashboard}
              onSearchFlight={handleSearchFlight}
              onBookFlightOpen={() => setBookFlightOpen(true)}
            />
          }
        />
      </Routes>
      <HubSelector
        isOpen={hubModalOpen}
        onClose={() => setHubModalOpen(false)}
        onSelectHub={handleHubSelect}
      />
      <BookFlight
        isOpen={bookFlightOpen}
        onClose={() => setBookFlightOpen(false)}
      />
    </>
  );
}

export default App;
